<template>
  <div class="w-full" style="height:100vh;display:flex;flex-direction:column">
    <!-- Header -->
    <div class="flex align-items-center gap-2 p-3 border-bottom-1 surface-border flex-none">
      <Button icon="pi pi-arrow-left" text severity="secondary" @click="goBack" />
      <div class="flex-1 text-left">
        <div class="text-lg font-medium">Monitor en vivo</div>
        <div v-if="tokenName" class="text-sm text-color-secondary">{{ tokenName }}</div>
      </div>
      <Tag
        :value="`${activeUsers.length} ${activeUsers.length === 1 ? 'persona' : 'personas'}`"
        :severity="activeUsers.length ? 'success' : 'secondary'"
      />
    </div>

    <!-- Selector de día -->
    <div v-if="salidas.length > 1" class="flex gap-2 px-3 py-2 overflow-x-auto flex-none surface-section border-bottom-1 surface-border">
      <Button
        v-for="(s, idx) in salidas"
        :key="idx"
        :label="`${getDayName(s.date)} ${getLocalDate(s.date)}`"
        size="small"
        :outlined="activeSalidaIdx !== idx"
        @click="activeSalidaIdx = idx"
      />
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Lista lateral -->
      <div class="border-right-1 surface-border overflow-auto flex-none" style="width:220px">
        <div v-if="!activeUsers.length" class="p-3 text-500 text-sm text-left">
          Sin usuarios compartiendo posición
        </div>
        <div
          v-for="u in activeUsers"
          :key="u.userName"
          class="flex align-items-center gap-2 p-3 cursor-pointer"
          style="border-bottom:1px solid var(--surface-border)"
          @click="centerOn(u)"
        >
          <div
            class="w-2rem h-2rem border-round-xl flex align-items-center justify-content-center font-bold text-white text-sm flex-shrink-0"
            :style="`background:${colorFor(u.userName)}`"
          >
            {{ u.userName.charAt(0).toUpperCase() }}
          </div>
          <div class="flex-1 text-left overflow-hidden">
            <div class="font-medium text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
              {{ u.userName }}
            </div>
            <div class="text-xs text-500">{{ formatAgo(u.ts) }}</div>
          </div>
        </div>
      </div>

      <!-- Mapa -->
      <div class="flex-1 relative">
        <div id="monitor-map" style="width:100%;height:100%" />
        <MapLayerControls
          :isSatellite="isSatellite"
          :google="googleEnabled"
          @toggle-satellite="toggleMapLayer"
          @toggle-google="toggleGoogle"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import L from 'leaflet'
import { useMapTiles } from '@/composables/useMapTiles'
import MapLayerControls from '@/components/MapLayerControls.vue'
import { DEFAULT_CENTER } from '@/store/mapConstants'

const route = useRouterStore()
const { getWsUrl, fetchPublicSalida } = useSalidaTokens()

const goBack = () => window.history.back()

const token = computed(() => route.params.token)
const tokenName = computed(() => route.params.name || '')

const activeUsers = ref([])
const salidas = ref([])
const activeSalidaIdx = ref(0)
const activeSalida = computed(() => salidas.value[activeSalidaIdx.value] || null)

let leafletMap = null
let wsConn = null
let peMarker = null

const { applyCurrentTiles, isSatellite, googleEnabled, toggle: toggleMapLayer, toggleGoogle } = useMapTiles(() => leafletMap)
const markerInstances = {}

const getDayName = (d) => {
  if (!d) return ''
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  return days[new Date(d).getDay()]
}

const getLocalDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
}

const toLatlng = (v) => {
  if (!v) return null
  if (Array.isArray(v)) return [v[0], v[1]]
  return [Number(v.lat), Number(v.lng)]
}

const updatePEMarker = () => {
  peMarker?.remove()
  peMarker = null
  if (!leafletMap) return
  const pe = activeSalida.value?.puntoEncuentro?.latlng
  if (!pe) return
  const ll = toLatlng(pe)
  if (!ll) return
  const icon = L.divIcon({
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    html: `<div style="width:26px;height:26px;background:#e07b39;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.3)"></div>`,
  })
  peMarker = L.marker(ll, { icon })
    .addTo(leafletMap)
    .bindPopup(`<b>Punto de encuentro</b><br>${activeSalida.value?.puntoEncuentro?.name || ''}`)
  leafletMap.setView(ll, 15)
}

watch(activeSalidaIdx, updatePEMarker)

const COLORS = ['#007aff', '#e07b39', '#2e8b57', '#9b59b6', '#c0392b', '#16a085', '#d35400', '#1a6b7a']

const colorFor = (name) => {
  const sum = [...name].reduce((a, c) => a + c.charCodeAt(0), 0)
  return COLORS[Math.abs(sum) % COLORS.length]
}

const formatAgo = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return `hace ${s}s`
  return `hace ${Math.floor(s / 60)}min`
}

const upsertUser = (loc) => {
  const ts = loc.ts || Date.now()
  const ll = [loc.lat, loc.lng]
  const idx = activeUsers.value.findIndex((u) => u.userName === loc.userName)
  if (idx >= 0) {
    activeUsers.value[idx].lat = loc.lat
    activeUsers.value[idx].lng = loc.lng
    activeUsers.value[idx].ts = ts
  } else {
    activeUsers.value.push({ userName: loc.userName, lat: loc.lat, lng: loc.lng, ts })
  }

  if (markerInstances[loc.userName]) {
    markerInstances[loc.userName].setLatLng(ll)
  } else if (leafletMap) {
    const color = colorFor(loc.userName)
    const initial = (loc.userName || '?').charAt(0).toUpperCase()
    const icon = L.divIcon({
      className: '',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      html: `<div style="width:36px;height:36px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:15px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${initial}</div>`,
    })
    markerInstances[loc.userName] = L.marker(ll, { icon, zIndexOffset: 800 })
      .addTo(leafletMap)
      .bindPopup(`<b>${loc.userName}</b>`)
  }
}

const removeUser = (userName) => {
  markerInstances[userName]?.remove()
  delete markerInstances[userName]
  activeUsers.value = activeUsers.value.filter((u) => u.userName !== userName)
}

const centerOn = (u) => {
  if (!leafletMap) return
  leafletMap.setView([u.lat, u.lng], Math.max(leafletMap.getZoom(), 16))
}

const fitToAll = () => {
  if (!leafletMap || !activeUsers.value.length) return
  const lls = activeUsers.value.map((u) => [u.lat, u.lng])
  if (lls.length === 1) {
    leafletMap.setView(lls[0], 16)
  } else {
    leafletMap.fitBounds(L.latLngBounds(lls), { padding: [40, 40] })
  }
}

const openWs = () => {
  wsConn = new WebSocket(getWsUrl(token.value))
  wsConn.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      if (msg.type === 'location') {
        upsertUser(msg)
      } else if (msg.type === 'leave') {
        removeUser(msg.userName)
      } else if (msg.type === 'snapshot') {
        msg.locations?.forEach(upsertUser)
        fitToAll()
      }
    } catch {}
  }
  wsConn.onclose = () => {
    if (token.value) setTimeout(openWs, 3000)
  }
}

onMounted(async () => {
  let initialCenter = DEFAULT_CENTER
  let initialZoom = 13
  try {
    const res = await fetchPublicSalida(token.value)
    if (res?.salidas?.length) {
      salidas.value = res.salidas
      const now = Date.now()
      let best = 0, bestDiff = Infinity
      res.salidas.forEach((s, i) => {
        const diff = Math.abs((s.date || 0) - now)
        if (diff < bestDiff) { bestDiff = diff; best = i }
      })
      activeSalidaIdx.value = best
      const pe = res.salidas[best]?.puntoEncuentro?.latlng
      if (pe) {
        const ll = toLatlng(pe)
        if (ll) { initialCenter = ll; initialZoom = 15 }
      }
    }
  } catch {}

  leafletMap = L.map('monitor-map').setView(initialCenter, initialZoom)
  applyCurrentTiles(leafletMap)
  updatePEMarker()
  openWs()
})

onUnmounted(() => {
  wsConn?.close()
  wsConn = null
  peMarker?.remove()
  peMarker = null
  leafletMap?.remove()
  leafletMap = null
})
</script>
